import React from 'react';
import { useSubscriptionByIdQuery } from '../../services/hooks/useSuscripciones';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { useSafeMounted } from '../../services/hooks/useSafeMounted';
import { useTenantCode } from '../../services/hooks/useTenantCode';
import { etiquetaSuscripcion } from '../../utils/suscripciones';

interface PagadorSuscripcionInfoProps {
  pagadorSuscripcionId: string;
}

const PagadorSuscripcionInfo: React.FC<PagadorSuscripcionInfoProps> = ({ pagadorSuscripcionId }) => {
    const isMounted = useSafeMounted();
    const tenantCode = useTenantCode();
    const { data: suscripcionPagador, isLoading, error } = useSubscriptionByIdQuery(
        pagadorSuscripcionId,
        {

            enabled: !!pagadorSuscripcionId && isMounted.current,
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000, // 5 minutos
            cacheTime: 60 * 60 * 1000, // 1 hora
            refetchOnMount: false,
            onError: () => {
                // Manejar solo si está montado
                if (!isMounted.current) return;
            }
        }
    );

    // Si el componente está desmontado, no renderizar nada
    if (!isMounted.current) return null;

    if (isLoading) {
        return (
        <div className="flex items-center text-sm text-gray-500">
            <Loader2 size={14} className="mr-2 animate-spin" />
            Cargando datos del pagador...
        </div>
        );
    }

    if (error || !suscripcionPagador) {
        return null;
        // return <div className="text-sm text-red-500">Error al cargar datos del pagador</div>;
    }

    // Extraer datos del cliente pagador de la estructura anidada
    const clientePagador = suscripcionPagador?.cliente_id;
    
    if (!clientePagador) {
        return null;
        // return <div className="text-sm text-red-500">Datos del cliente pagador no disponibles</div>;
    }

    return (
        <div className="mt-2 p-3 bg-green-50 rounded-md">
            <div className="flex justify-between items-start">
                <h4 className="font-semibold text-green-800 mb-1">Cliente Pagador</h4>
                {/* Las rutas de la app viven bajo /:tenantCode. Sin el prefijo,
                    "clientes" se interpreta como el código de centro y la ficha
                    no carga. */}
                <Link
                    to={`/${tenantCode}/clientes/${clientePagador._id}`}
                    className="text-green-700 hover:text-green-900 text-sm flex items-center"
                >
                    Ver ficha completa <ExternalLink size={14} className="ml-1" />
                </Link>
            </div>
            <p><strong>Nombre:</strong> {clientePagador.nombre}</p>
            <p><strong>DNI:</strong> {clientePagador.dni}</p>
            <p><strong>Teléfono:</strong> {clientePagador.telefono}</p>
            {clientePagador.email && <p><strong>Email:</strong> {clientePagador.email}</p>}
            
            {/* Información de la suscripción pagadora */}
            <div className="mt-2 pt-2 border-t border-green-200">
                <p className="font-medium text-green-800">Datos de pago:</p>
                {suscripcionPagador?.iban && (
                    <p><strong>IBAN:</strong> {suscripcionPagador.iban}</p>
                )}
                <p><strong>Suscripción:</strong> {etiquetaSuscripcion(suscripcionPagador.nombreSuscripcion)}</p>
                <p><strong>Importe:</strong> {suscripcionPagador.importeSuscripcion}€</p>
            </div>
        </div>
    );
};

// Usar memo para evitar re-renderizados innecesarios
export default React.memo(PagadorSuscripcionInfo);